/**
 * auth.js
 *
 * Pinia store for managing user authentication, including login, logout, and user data retrieval.
 */

import { defineStore } from 'pinia'
import { log } from '@/plugins/logger'
import { auth, db } from '@/plugins/firebase'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  browserLocalPersistence,
  signOut,
  setPersistence
} from 'firebase/auth'
import { getDoc, doc, updateDoc } from 'firebase/firestore'
import { useTenantStore } from '@/store/tenant'
import analytics from '@/plugins/analyticsRelay'
import { httpHydra } from '@/api'
import { jwtDecode } from 'jwt-decode'
import { paragon } from '@useparagon/connect'
import { SUPPORT_EMAIL, SUPPORT_TENANT_ID } from '@/config/constants'
import { setSessionCookie, clearSessionCookie } from '@/utils/sessionTimeout'

const storesWithAuthListener = new WeakSet()
let authStateGeneration = 0

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null, // Firebase user object
    userToken: null, // JWT token for the user
    userData: null, // Additional user data from Firestore
    hydraToken: null, // Paragon authentication token
    hydraTokenExpiry: null, // Expiration timestamp for Paragon token
    paragonUser: null, // Paragon user object
    impersonationActive: false, // Flag to indicate if support impersonation is active
    originalUserData: null, // Store original user data when impersonating
    viewAsUserData: null // When null, normal behavior; when set, "viewAs" mode is active
  }),
  getters: {
    /**
     * isViewingAs
     *
     * Determines if the user is currently in viewAs mode
     *
     * @returns {boolean} True if the user is in viewAs mode
     */
    isViewingAs() {
      return !!this.viewAsUserData
    },

    /**
     * userIsManager
     *
     * Determines if the user has manager permissions
     *
     * @returns {boolean} True if the user has manager permissions
     */
    userIsManager() {
      // some userData objects will not have the `is_manager` property, so we must check for its presence
      return this.userData?.is_manager === true
    },

    /**
     * userIsDisabled
     *
     * Determines if disabled-account restrictions apply
     *
     * @returns {boolean} True if the user's account is disabled and not being impersonated
     */
    userIsDisabled() {
      return !this.impersonationActive && !!this.userData?.disabled
    },

    /**
     * userHasEmailPreferences
     *
     * Determines if the user has any email preferences set
     *
     * @returns {Object} An object with properties rules and example_emails, both boolean
     */
    userHasEmailPreferences() {
      const aiPreferences = this.userData?.preferences?.ai_email_preferences

      // Check if ai_email_preferences exists and is an object
      if (!aiPreferences) {
        return {
          rules: false,
          example_emails: false
        }
      }

      return {
        rules: aiPreferences?.rules?.length > 0,
        example_emails: aiPreferences?.example_emails?.length > 0
      }
    }
  },
  actions: {
    _authSnapshot() {
      return {
        generation: authStateGeneration,
        authUid: this.user?.uid,
        impersonating: this.impersonationActive
      }
    },

    _authSnapshotChanged(snapshot) {
      return (
        snapshot.generation !== authStateGeneration ||
        snapshot.authUid !== this.user?.uid ||
        snapshot.impersonating !== this.impersonationActive
      )
    },

    /**
     * _firebaseAuthSignIn
     *
     * Signs in the user using Firebase Authentication with email and password.
     *
     * @param {string} email - User's email address
     * @param {string} password - User's password
     * @returns {Promise<firebase.auth.UserCredential>} - Firebase user credentials
     */
    async _firebaseAuthSignIn(email, password) {
      try {
        await setPersistence(auth, browserLocalPersistence)
        const userCredential = await signInWithEmailAndPassword(auth, email, password)
        return userCredential
      } catch (e) {
        throw e
      }
    },

    /**
     * firebaseAuthSignOut
     *
     * Signs out the user from Firebase Authentication and resets analytics data.
     */
    async firebaseAuthSignOut() {
      try {
        analytics.reset()
        await signOut(auth)
      } catch (e) {
        log.error('Error signing out user:', e)
        throw e
      }
    },

    /**
     * loginUser
     *
     * Handles the user login process, including authentication and fetching user data.
     *
     * @param {string} email - User's email address
     * @param {string} password - User's password
     */
    async loginUser(email, password) {
      try {
        const userCredential = await this._firebaseAuthSignIn(email, password)
        setSessionCookie()
        this.user = userCredential.user
        this.userToken = await this.user.getIdToken(true)
        await this.getUserData()
      } catch (error) {
        throw error
      }
    },

    /**
     * _fetchUserDataByUid
     *
     * Fetches user data from Firestore for a specific UID without modifying state
     *
     * @param {string} uid - The user ID to fetch data for
     * @returns {Promise<Object|null>} - User data object or null if not found
     * @private
     */
    async _fetchUserDataByUid(uid) {
      try {
        const userDoc = doc(db, 'users', uid)
        const docSnap = await getDoc(userDoc)
        if (docSnap.exists()) {
          return docSnap.data()
        }
        log.error('No userData document found for user:', uid)
        return null
      } catch (e) {
        log.error('Error fetching user data:', e)
        throw e
      }
    },

    /**
     * getUserData
     *
     * Retrieves additional user data from Firestore based on the authenticated user's UID.
     */
    async getUserData() {
      try {
        const authSnapshot = this._authSnapshot()
        const userData = await this._fetchUserDataByUid(authSnapshot.authUid)

        if (this._authSnapshotChanged(authSnapshot)) return

        if (userData) {
          this.userData = userData
        } else {
          await this.logoutUser()
        }
      } catch (e) {
        log.error('Error fetching user data:', e)
        throw e
      }
    },

    /**
     * refreshUserData
     *
     * Refreshes the user data by re-fetching it from Firestore.
     * When impersonating a user, refreshes the impersonated user's data.
     */
    async refreshUserData() {
      // For normal users, just call getUserData
      if (!this.impersonationActive) {
        await this.getUserData()
        return
      }

      // For impersonated users, fetch their data directly
      try {
        const authSnapshot = this._authSnapshot()
        const expectedUserDataUid = this.userData.uid
        const userData = await this._fetchUserDataByUid(expectedUserDataUid)

        if (this._authSnapshotChanged(authSnapshot) || this.userData?.uid !== expectedUserDataUid) {
          return
        }

        if (userData) {
          // Preserve impersonation flags while updating other data
          this.userData = userData
        } else {
          log.error('No data found for impersonated user:', expectedUserDataUid)
        }
      } catch (e) {
        log.error('Error refreshing impersonated user data:', e)
        throw e
      }
    },

    /**
     * _getTokenExpiry
     *
     * Gets the expiration time from a JWT token
     * @param {string} token - JWT token
     * @param {number} defaultExpiry - Default expiration time to use if token cannot be decoded
     * @returns {number} - Expiration time in seconds
     * @private
     */
    _getTokenExpiry(token, defaultExpiry) {
      try {
        const payload = jwtDecode(token)
        return payload?.exp || defaultExpiry
      } catch (error) {
        log.error('Error decoding JWT payload:', error)
        return defaultExpiry
      }
    },

    /**
     * getHydraToken
     *
     * Gets a valid Paragon authentication token using the Hydra JWT Generator service.
     * Caches the token until it's close to expiration.
     *
     * @returns {Promise<string>} - JWT token for Paragon authentication
     */
    async getHydraToken() {
      try {
        const now = Math.floor(Date.now() / 1000)
        const EXPIRY_BUFFER = 300 // 5 minutes in seconds

        // Return cached token if still valid
        if (
          this.hydraToken &&
          this.hydraTokenExpiry &&
          now < this.hydraTokenExpiry - EXPIRY_BUFFER
        ) {
          // Check if Paragon user is authenticated before returning cached token
          await this._updateParagonUserAfterTokenRefresh(this.hydraToken)
          return this.hydraToken
        }

        // Get tenant store and ensure master_tenant_id is available
        const tenantStore = useTenantStore()

        // Load company config if not already loaded
        if (!tenantStore.companyConfig) {
          await tenantStore.getCompanyConfig()
        }

        // Verify master tenant ID availability
        if (!tenantStore.companyConfig?.master_tenant_id) {
          throw new Error('Master tenant ID not available')
        }

        // Request new token from Hydra service
        const response = await httpHydra.post('/HydraJwtGenerator', {
          tenant_id: tenantStore.companyConfig.master_tenant_id,
          user_id: this.userData?.uid
        })

        const token = response.data.token
        const TOKEN_EXPIRY_SECONDS = 3600 // Default to 1 hour from now
        const defaultExpiry = now + TOKEN_EXPIRY_SECONDS

        // Parse token and get expiration
        this.hydraTokenExpiry = this._getTokenExpiry(token, defaultExpiry)
        this.hydraToken = token

        // Since we've refreshed the token, update the Paragon user state
        // We wait for this to complete to ensure downstream functions have
        // both the new token and the updated Paragon user
        await this._updateParagonUserAfterTokenRefresh(token)

        return token
      } catch (error) {
        log.error('Error getting Hydra token', error)
        throw error
      }
    },

    /**
     * _updateParagonUserAfterTokenRefresh
     *
     * Updates the Paragon user state after a token refresh
     * This is a private method to avoid circular dependencies between
     * getHydraToken and getParagonUser
     *
     * @param {string} token - The new Hydra token
     * @private
     */
    async _updateParagonUserAfterTokenRefresh(token) {
      try {
        const currentUser = paragon.getUser()

        // Only re-authenticate if we have a new token and either:
        // 1. There is no current user, or
        // 2. The current user is not authenticated
        if (!currentUser?.authenticated) {
          await paragon.authenticate(import.meta.env.VITE_PARAGON_PROJECT_ID, token)

          // Get the latest user state after authentication
          const updatedUser = paragon.getUser()
          this.setParagonUser(updatedUser)
        }
      } catch (error) {
        log.error('Failed to update Paragon user after token refresh:', error)
      }
    },

    /**
     * clearHydraToken
     *
     * Clears the cached Paragon token, forcing a new token to be generated
     * on the next call to getHydraToken().
     * Also clears Paragon-related data from storage.
     */
    clearHydraToken() {
      this.hydraToken = null
      this.hydraTokenExpiry = null
      this.paragonUser = null

      // Clear Paragon's cached user state from localStorage
      localStorage.removeItem('paragon-connect-user-state')

      // Clear any other Paragon-related items from localStorage
      // This is the most important storage location for Paragon SDK
      Object.keys(localStorage).forEach(key => {
        if (key.includes('paragon') || key.includes('useparagon')) {
          localStorage.removeItem(key)
        }
      })

      // Clear session storage items
      try {
        sessionStorage.removeItem('paragon-connect-user-state')

        // Also clear any other Paragon-related items from sessionStorage
        Object.keys(sessionStorage).forEach(key => {
          if (key.includes('paragon') || key.includes('useparagon')) {
            sessionStorage.removeItem(key)
          }
        })
      } catch (e) {
        log.error('Error clearing session storage:', e)
      }

      // Clear cookies for the useparagon.com domain
      try {
        // Get all cookies
        const cookies = document.cookie.split(';')

        // Find and clear any Paragon-related cookies
        cookies.forEach(cookie => {
          const cookieParts = cookie.split('=')
          const cookieName = cookieParts[0].trim()

          if (cookieName.includes('paragon') || cookieName.includes('useparagon')) {
            // In JavaScript, cookies are deleted by setting them with an expired date
            // These lines set each matching cookie with an expiration date in the past
            // We try multiple domain variations to ensure the cookie is properly removed
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.useparagon.com`
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=connect.useparagon.com`
          }
        })
      } catch (e) {
        log.error('Error clearing cookies:', e)
      }
    },

    /**
     * setParagonUser
     *
     * Sets the Paragon user data in the store.
     * This should be called when retrieving user data from Paragon.
     *
     * @param {Object} user - Paragon user object
     */
    setParagonUser(user) {
      this.paragonUser = user
    },

    /**
     * getParagonUser
     *
     * Gets the Paragon user, ensuring authentication if needed.
     * This is the main method for handling Paragon authentication.
     *
     * @returns {Promise<Object|null>} - Paragon user object or null if authentication fails
     */
    async getParagonUser() {
      try {
        // Store the current token before potentially refreshing it
        const currentToken = this.hydraToken

        // Get a valid token - getHydraToken handles validation and refresh
        const token = await this.getHydraToken()

        // Check if the token was refreshed and if we have an authenticated user
        const tokenWasRefreshed = currentToken !== token

        const currentUser = paragon.getUser()

        // Only authenticate if the token was refreshed or the user isn't authenticated
        if (tokenWasRefreshed || !currentUser?.authenticated) {
          await paragon.authenticate(import.meta.env.VITE_PARAGON_PROJECT_ID, token)

          // Get the latest user state after authentication
          const updatedUser = paragon.getUser()
          this.setParagonUser(updatedUser)
          return updatedUser
        }

        // If we have a valid token and authenticated user, just update the store and return
        this.setParagonUser(currentUser)
        return currentUser
      } catch (error) {
        log.error('Failed to get Paragon user:', error)
        return null
      }
    },

    /**
     * refreshParagonUser
     *
     * Refreshes the Paragon user by clearing the token and re-authenticating.
     *
     * @returns {Promise<Object|null>} - Updated Paragon user or null if refresh fails
     */
    async refreshParagonUser() {
      try {
        // Reset the cached token to force a new one
        this.clearHydraToken()

        // Get a fresh user
        return await this.getParagonUser()
      } catch (error) {
        log.error('Failed to refresh Paragon user:', error)
        return null
      }
    },

    /**
     * logoutUser
     *
     * Logs out the user, clears tenant configurations, and resets authentication state.
     */
    async logoutUser() {
      try {
        await this.firebaseAuthSignOut()
        this.resetAuthState()
      } catch (e) {
        log.error('Error logging out user:', e)
        throw e
      }
    },

    /**
     * updateUserField
     *
     * Updates a specific field in the user's Firestore document.
     *
     * @param {string} field - The field to update
     * @param {any} value - The new value for the field
     * @returns {Promise<boolean>} - Returns true if the update is successful
     */
    async updateUserField(field, value) {
      try {
        const userDoc = doc(db, 'users', this.userData.uid)
        await updateDoc(userDoc, { [field]: value })
        return true
      } catch (e) {
        log.error(`Error updating user field (${field}):`, e)
        return false
      }
    },

    /**
     * bindUser
     *
     * Binds the authentication state by setting up a Firebase auth state listener.
     * Ensures that only one listener is active to prevent memory leaks.
     *
     * @returns {Promise<firebase.auth.User|null>} - Resolves with the user object or null
     */
    bindUser() {
      return new Promise((resolve, reject) => {
        if (storesWithAuthListener.has(this)) {
          resolve(this.user)
          return
        }

        storesWithAuthListener.add(this)
        let unsubscribe

        try {
          unsubscribe = onAuthStateChanged(
            auth,
            async user => {
              try {
                if (this.user?.uid !== user?.uid) {
                  authStateGeneration++
                }
                if (!user) {
                  this.resetAuthState()
                  resolve(null)
                  return
                }

                this.user = user
                if (this.impersonationActive) {
                  resolve(this.user)
                  return
                }

                if (this.userData) {
                  resolve(this.user)
                  this.getUserData().catch(error => {
                    log.error('Error refreshing cached user data:', error)
                  })
                  return
                }

                await this.getUserData()
                resolve(this.user)
              } catch (error) {
                log.error('Error binding user data:', error)
                resolve(this.user)
              }
            },
            error => {
              unsubscribe?.()
              storesWithAuthListener.delete(this)
              log.error('Auth state change error:', error)
              reject(error)
            }
          )
        } catch (error) {
          storesWithAuthListener.delete(this)
          reject(error)
        }
      })
    },

    /**
     * resetAuthState
     *
     * Resets the authentication state, clearing user information and tenant configurations.
     */
    resetAuthState() {
      authStateGeneration++
      const tenantStore = useTenantStore()
      tenantStore.clearCompanyConfig()
      this.clearHydraToken()
      clearSessionCookie()

      // Reset Paragon state by clearing the token
      // The SDK will require re-authentication on next use
      try {
        // Force Paragon to re-authenticate on next use by removing the token
        localStorage.removeItem('paragon-connect-user-state')
        localStorage.removeItem('paragon-connect-token')
      } catch (e) {
        log.error('Error clearing Paragon state:', e)
      }

      this.user = null
      this.userToken = null
      this.userData = null
      this.impersonationActive = false
      this.originalUserData = null
      this.viewAsUserData = null
    },

    /**
     * startImpersonation
     *
     * Starts impersonating a user by their UID
     *
     * @param {string} mockUid - The UID of the user to impersonate
     * @returns {Promise<boolean>} - Returns true if impersonation is successful
     */
    async startImpersonation(mockUid) {
      try {
        if (!this.user || !this.userData) {
          log.error('Cannot impersonate: No authenticated support user')
          return false
        }

        // Store original user data before impersonation
        this.originalUserData = { ...this.userData }

        // Only allow impersonation if user is explicitly the support email
        // Check both user and userData for belt-and-suspenders security
        if (this.user.email === SUPPORT_EMAIL && this.userData.email === SUPPORT_EMAIL) {
          authStateGeneration++
          const authSnapshot = this._authSnapshot()
          const userDoc = doc(db, 'users', mockUid)
          const docSnap = await getDoc(userDoc)

          if (this._authSnapshotChanged(authSnapshot)) return false

          if (!docSnap.exists()) {
            log.error('No user found with UID:', mockUid)
            return false
          }

          // Clear Hydra token and Paragon user to ensure fresh authentication for impersonated user
          this.clearHydraToken()

          // Replace userData with the impersonated user's data
          this.userData = docSnap.data()
          this.impersonationActive = true

          // Refresh tenant data for the impersonated user
          const tenantStore = useTenantStore()
          await tenantStore.getCompanyConfig()

          log.info(`Support impersonation started for user: ${mockUid}`)
          return true
        } else {
          log.error('Impersonation not allowed for this user')
          return false
        }
      } catch (e) {
        log.error('Error starting impersonation:', e)
        return false
      }
    },

    /**
     * stopImpersonation
     *
     * Stops impersonating a user and restores original user data
     *
     * @returns {Promise<boolean>} - Returns true if stopping impersonation is successful
     */
    async stopImpersonation() {
      try {
        if (!this.impersonationActive || !this.originalUserData) {
          return false
        }

        authStateGeneration++

        // Clear Hydra token and Paragon user to ensure fresh authentication for original user
        this.clearHydraToken()

        // Restore original user data
        this.userData = this.originalUserData
        this.originalUserData = null
        this.impersonationActive = false

        // Refresh tenant data for the original user
        const tenantStore = useTenantStore()
        await tenantStore.getCompanyConfig()

        log.info('Support impersonation ended')
        return true
      } catch (e) {
        log.error('Error stopping impersonation:', e)
        return false
      }
    },

    /**
     * startViewAs
     *
     * Starts viewing the application as if the manager was another user
     * This is different from impersonation because actions are still logged to the manager's account
     *
     * @param {Object} targetUserDataObject - The user data object of the user to view as
     * @returns {boolean} - Returns true if starting viewAs is successful
     */
    startViewAs(targetUserDataObject) {
      if (!targetUserDataObject) return false
      this.viewAsUserData = targetUserDataObject
      log.info(`View as started for user: ${targetUserDataObject.uid}`)
      return true
    },

    /**
     * stopViewAs
     *
     * Stops viewing as another user and returns to normal view
     *
     * @returns {boolean} - Returns true if stopping viewAs is successful
     */
    stopViewAs() {
      if (!this.viewAsUserData) return false
      this.viewAsUserData = null
      log.info('View as stopped')
      return true
    }
  },
  persist: {
    paths: [
      'user',
      'userToken',
      'userData',
      'paragonUser',
      'impersonationActive',
      'originalUserData',
      'viewAsUserData'
      // Excluding hydraToken and hydraTokenExpiry for security
    ]
  }
})
